/**
 * Fused tracks feed (ENG-341) — `surveillance_traffic/fused_tracks/<id>`.
 *
 * The platform's own fusion of live_ads / live_mlat / live_community. Nominally
 * one message per second (in practice bursty), each a JSON array holding the
 * full current picture (~160 aircraft), so every message REPLACES the previous
 * picture rather than merging into it. The id segment is a fixed constant, the same on every
 * tenant.
 *
 * Units: `speed` is m/s, `track` is degrees true, `alt_m` is metres. Each
 * element's `source` is only the feed that supplied the latest update, not
 * every feed that contributed, and there is no category / type / registration.
 */
import type { Target } from '@/lib/mqtt/schemas/targets';
import { z } from 'zod';
import { trustFromEmitter } from './surveillanceTraffic';

export const COMMUNITY_EMITTER = 'live_community';
export const COMMUNITY_TOPIC = `surveillance_traffic/${COMMUNITY_EMITTER}`;

export const FUSED_TRACKS_TOPIC =
  'surveillance_traffic/fused_tracks/0daa0000-0000-4000-8000-000000000daa';

/** The raw feeds the platform fuses into a track (Brayden, ENG-342). The
 *  regional feeds are not inputs, so they get no circle. */
export const FUSED_INPUT_FEEDS: readonly string[] = ['live_ads', 'live_mlat', 'live_community'];

/** A raw feed's last reported position for one aircraft. */
export interface SourcePosition {
  service_id: string;
  lat: number;
  lng: number;
  /** Epoch ms the feed's report arrived — for "seen N s ago". */
  at: number;
}

/** Nominally 1 Hz, but measured on airmarket (2026-11-03) it arrives in bursts —
 *  about one message per 4.5 s, with gaps of up to 16 s. Past this with nothing
 *  new it is considered gone; shorter and the map flips to the raw fallback — and
 *  loses its circles — between bursts. */
export const FUSED_STALE_MS = 30_000;

/** The feeds send 361 for "heading unknown" (README, Brayden's viewer); like a
 *  missing track it falls back to 0 rather than being drawn as a real 1°. */
const HEADING_UNKNOWN = 361;
const headingFrom = (track: number | null | undefined): number =>
  track == null || track === HEADING_UNKNOWN ? 0 : ((track % 360) + 360) % 360;

const M_PER_S_TO_KTS = 1.943844;
const M_TO_FT = 3.28084;

const FusedTrackWire = z.object({
  unique_aircraft_identifier: z.string().min(1),
  callsign: z.string().nullish(),
  state: z.object({
    position: z.object({
      lat: z.number().min(-90).max(90),
      lng: z.number().min(-180).max(180),
      alt_m: z.number().nullish(),
    }),
    speed: z.number().nullish(),
    track: z.number().nullish(),
  }),
  track_state: z.string().nullish(),
  source: z.string().nullish(),
});

export interface FusedTrack {
  /** Lowercase ICAO hex. */
  hex: string;
  callsign: string | null;
  lat: number;
  lng: number;
  altitudeFt: number;
  speedKts: number;
  heading: number;
  coasting: boolean;
  source: string | null;
}

/** Wire message → tracks. Null when it isn't an array; unusable elements are skipped. */
export function parseFusedTracks(raw: unknown): FusedTrack[] | null {
  if (!Array.isArray(raw)) return null;
  const out: FusedTrack[] = [];
  for (const item of raw) {
    const r = FusedTrackWire.safeParse(item);
    if (!r.success) continue;
    const w = r.data;
    const { position, speed, track } = w.state;
    out.push({
      hex: w.unique_aircraft_identifier.toLowerCase(),
      callsign: w.callsign?.trim() || null,
      lat: position.lat,
      lng: position.lng,
      altitudeFt: Math.round((position.alt_m ?? 0) * M_TO_FT),
      speedKts: Math.round((speed ?? 0) * M_PER_S_TO_KTS * 10) / 10,
      heading: headingFrom(track),
      coasting: w.track_state === 'Coasting',
      source: w.source ?? null,
    });
  }
  return out;
}

/**
 * The aircraft the map shows: the fused tracks, plus RF-detected drones (they
 * are not in the fused feed). The raw per-emitter merge is used only to carry
 * over what the fused message lacks — category, type, registration,
 * description, trust and the full list of contributing feeds — matched by ICAO
 * hex, and it keeps its `target_id` so selection and trails stay stable.
 * Aircraft only a raw feed sees are dropped. `sourcePositions` (by hex) is where
 * each raw feed last put the aircraft, for the Surveillance tab's circles.
 */
export function mergeFusedTracks(
  raw: Target[],
  tracks: FusedTrack[],
  receivedAtIso: string,
  sourcePositions?: ReadonlyMap<string, SourcePosition[]>,
): Target[] {
  const rawByHex = new Map<string, Target>();
  for (const t of raw) if (t.icao_hex) rawByHex.set(t.icao_hex.toLowerCase(), t);

  const out: Target[] = raw.filter((t) => t.rf != null);
  const used = new Set(out.map((t) => t.target_id));

  for (const f of tracks) {
    const match = rawByHex.get(f.hex);
    let id = match?.target_id ?? f.callsign ?? f.hex;
    if (used.has(id)) id = f.hex; // two aircraft sharing a callsign
    if (used.has(id)) continue;
    used.add(id);

    out.push({
      target_id: id,
      callsign: f.callsign ?? match?.callsign ?? f.hex.toUpperCase(),
      icao_hex: f.hex,
      lat: f.lat,
      lng: f.lng,
      heading: f.heading,
      altitude_ft: f.altitudeFt,
      speed_kts: f.speedKts,
      type: match?.type ?? 'medium',
      trust: match?.trust ?? trustFromEmitter(f.source ?? ''),
      source_services: [
        ...new Set([...(match?.source_services ?? []), ...(f.source ? [f.source] : [])]),
      ],
      received_at: receivedAtIso,
      category: match?.category ?? null,
      aircraft_type: match?.aircraft_type ?? null,
      registration: match?.registration ?? null,
      description: match?.description ?? null,
      rf: null,
      coasting: f.coasting,
      sources: sourcePositions?.get(f.hex) ?? [],
      dist_km: null,
      vert_sep_ft: null,
      horiz_sep_nm: null,
      proximity: 'safe',
    });
  }
  return out;
}
